import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
// NOTE: ForeverWall3D intentionally not imported — the profile page now
// renders the LivingCast layout. ForeverWall3D.tsx stays in the repo for
// the overlay/demo and a possible future re-introduction.
import LivingCast from "../components/LivingCast";
import "./Profile.css";

interface ProfileFaction {
  id: string;
  slug: string;
  name: string;
  color: string;
  position: number;
  total: number;
}

interface ProfileDonation {
  id: string;
  donor_name: string;
  donor_handle?: string;
  amount: number;
  message: string | null;
  faction_id: string | null;
  character_id?: string | null;
  source?: "live" | "profile";
  created_at: number;
}

interface ForeverTotemRow {
  id: string;
  donor_name: string;
  donor_handle: string;
  amount: number;
  message: string | null;
  faction_id: string | null;
  character_id?: string | null;
  created_at: number;
}

interface ProfileResp {
  streamer: {
    handle: string;
    display_name: string;
    avatar_url: string | null;
    bio: string | null;
    overlay_key: string;
    platform: "twitch" | "youtube" | "unknown";
  };
  factions: ProfileFaction[];
  donations: ProfileDonation[];
  // Aggregated permanent donations grouped by donor_handle+faction.
  // Optional for back-compat with deploys that pre-date the aggregation.
  forever_totems?: ForeverTotemRow[];
  stats: {
    total_raised: number;
    supporter_count: number;
    top_donor: string | null;
    donation_count: number;
  };
}

const FACTION_GLYPH: Record<string, string> = {
  crim: "▲",
  cob: "◆",
  tox: "✚",
  volt: "⚡",
  myth: "✦",
};

function timeAgo(ts: number): string {
  const diff = Math.max(0, Date.now() - ts);
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

export default function Profile(): JSX.Element {
  const { handle = "" } = useParams<{ handle: string }>();
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ["streamer-profile", handle],
    queryFn: () => api.get<ProfileResp>(`/api/streamer/${handle}`),
    enabled: handle.length > 0,
  });

  // Live updates: subscribe to the DO wall-events socket. On every permanent
  // donation push, refetch the profile so the Forever Wall re-aggregates and
  // either spawns a new totem or fattens an existing one.
  const wsRef = useRef<WebSocket | null>(null);
  useEffect(() => {
    if (!handle) return;
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${location.host}/api/streamer/${encodeURIComponent(handle)}/wall-events`;
    let closed = false;
    let retry: ReturnType<typeof setTimeout> | null = null;
    const connect = (): void => {
      if (closed) return;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data) as { type?: string };
          if (msg.type === "donation_paid" || msg.type === "donation") {
            queryClient.invalidateQueries({ queryKey: ["streamer-profile", handle] });
          }
        } catch {
          // ignore non-json frames
        }
      };
      ws.onclose = () => {
        if (closed) return;
        // Light backoff so we don't hammer the worker if the binding is down.
        retry = setTimeout(connect, 4000);
      };
      ws.onerror = () => {
        try { ws.close(); } catch { /* noop */ }
      };
    };
    connect();
    return () => {
      closed = true;
      if (retry) clearTimeout(retry);
      try { wsRef.current?.close(); } catch { /* noop */ }
    };
  }, [handle, queryClient]);

  const factionRanks = useMemo(() => {
    if (!q.data) return [];
    const max = Math.max(1, ...q.data.factions.map((f) => f.total));
    const total = q.data.factions.reduce((s, f) => s + f.total, 0) || 1;
    return [...q.data.factions]
      .sort((a, b) => b.total - a.total)
      .map((f) => ({
        ...f,
        widthPct: Math.max(2, Math.round((f.total / max) * 100)),
        sharePct: Math.round((f.total / total) * 100),
      }));
  }, [q.data]);

  const recent = useMemo(() => {
    return [...(q.data?.donations ?? [])]
      .sort((a, b) => b.created_at - a.created_at)
      .slice(0, 10);
  }, [q.data]);

  const factionMap = useMemo(() => {
    const m = new Map<string, ProfileFaction>();
    for (const f of q.data?.factions ?? []) m.set(f.id, f);
    return m;
  }, [q.data]);

  if (q.isLoading) {
    return (
      <div className="prof-wrap">
        <p style={{ padding: 48, fontFamily: "var(--f-mono)", color: "var(--text-mute)" }}>
          LOADING STREAMER…
        </p>
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="prof-wrap">
        <div className="prof-notfound">
          <div className="prof-nf-glyph">404</div>
          <h1>STREAMER NOT FOUND</h1>
          <p>
            No wall lives at <code>/{handle}</code>. The handle may be wrong,
            or the streamer hasn&apos;t claimed it yet.
          </p>
          <div className="prof-nf-ctas">
            <Link to="/" className="prof-nf-btn prof-nf-btn-primary">← BACK HOME</Link>
            <Link to="/demo" className="prof-nf-btn">SEE A DEMO</Link>
          </div>
        </div>
      </div>
    );
  }

  const { streamer, stats } = q.data;
  const initial = (streamer.display_name || streamer.handle || "?")[0].toUpperCase();

  return (
    <div className="prof-wrap">
      {/* Hero */}
      <section className="prof-hero">
        <div className="prof-hero-row">
          <div className="prof-av">
            <div className="prof-av-inner">
              {streamer.avatar_url ? <img src={streamer.avatar_url} alt="" /> : initial}
            </div>
          </div>
          <div className="prof-meta">
            <h1>
              {streamer.display_name.toUpperCase()}{" "}
              <span className="prof-platform">
                {streamer.platform === "twitch"
                  ? "TWITCH"
                  : streamer.platform === "youtube"
                  ? "YOUTUBE"
                  : "STREAMER"}
              </span>
            </h1>
            <div className="prof-handle">@{streamer.handle}</div>
            {streamer.bio ? <p className="prof-bio">{streamer.bio}</p> : null}
            <div className="prof-stats">
              <div className="prof-stat">
                <div className="v gold">
                  ${stats.total_raised.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </div>
                <div className="l">ALL-TIME WALL</div>
              </div>
              <div className="prof-stat">
                <div className="v">{stats.donation_count.toLocaleString()}</div>
                <div className="l">SIGNATURES</div>
              </div>
              <div className="prof-stat">
                <div className="v">{stats.supporter_count.toLocaleString()}</div>
                <div className="l">SUPPORTERS</div>
              </div>
              {stats.top_donor ? (
                <div className="prof-stat">
                  <div className="v">{stats.top_donor}</div>
                  <div className="l">TOP DONOR</div>
                </div>
              ) : null}
            </div>
          </div>
          <div className="prof-cta">
            <Link to={`/${streamer.handle}/donate`} className="btn-big">
              SIGN THE WALL ▶
            </Link>
            <div className="mini">FOREVER · NEVER DECAYS</div>
            <a
              href={`/overlay/index.html?key=${encodeURIComponent(streamer.overlay_key)}`}
              target="_blank"
              rel="noreferrer"
              style={{
                color: "var(--text-sec)",
                fontSize: 11,
                fontFamily: "var(--f-mono)",
                letterSpacing: ".12em",
                textTransform: "uppercase",
              }}
            >
              ⚡ Watch the live wall ↗
            </a>
          </div>
        </div>
      </section>

      {/* Living Cast — the new hero artifact: faction leaderboard, stage
          with bobbing donor characters, stats, and memory-graph stub. */}
      <div className="prof-section-h">
        <h2>
          THE <span>LIVING CAST</span>
        </h2>
        <div className="meta">
          {stats.donation_count.toLocaleString()} SIGNATURES · {stats.supporter_count.toLocaleString()} CHARACTERS
        </div>
      </div>
      <div className="prof-forever">
        {q.data.donations.length > 0 ? (
          <LivingCast
            donations={q.data.donations}
            factions={q.data.factions}
            stats={{
              total_raised: stats.total_raised,
              supporter_count: stats.supporter_count,
              donation_count: stats.donation_count,
            }}
          />
        ) : (
          <div className="prof-mosaic-foot" style={{ padding: 40 }}>
            NO SIGNATURES YET · BE THE FIRST
          </div>
        )}
      </div>

      {/* Lifetime factions */}
      {factionRanks.length > 0 ? (
        <>
          <div className="prof-section-h">
            <h2>
              LIFETIME <span>FACTIONS</span>
            </h2>
            <div className="meta">{stats.donation_count.toLocaleString()} SIGNATURES · ALL TIME</div>
          </div>
          <div className="prof-panel">
            {factionRanks.map((f) => (
              <div
                key={f.id}
                className={`lf-row f-${f.slug}`}
                style={{ ["--c" as string]: f.color, ["--g" as string]: f.color, ["--t" as string]: f.color }}
              >
                <div className="glyph">{FACTION_GLYPH[f.slug] ?? "●"}</div>
                <div className="name">{f.name.toUpperCase()}</div>
                <div className="bar"><i style={{ width: `${f.widthPct}%` }} /></div>
                <div className="amt">
                  ${f.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </div>
                <div className="pct">{f.sharePct}%</div>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {/* Recent drops */}
      {recent.length > 0 ? (
        <>
          <div className="prof-section-h">
            <h2>
              RECENT <span>DROPS</span>
            </h2>
            <div className="meta">LATEST {recent.length} SIGNATURES</div>
          </div>
          <div className="prof-panel prof-recent">
            {recent.map((d) => {
              const fac = d.faction_id ? factionMap.get(d.faction_id) : null;
              const slug = fac?.slug ?? "crim";
              const color = fac?.color ?? "#FF1744";
              return (
                <div
                  key={d.id}
                  className={`r f-${slug}`}
                  style={{ ["--c" as string]: color, ["--g" as string]: color }}
                >
                  <div className="g">{FACTION_GLYPH[slug] ?? "●"}</div>
                  <div className="who">
                    {d.donor_name}
                    {d.message ? <small>"{d.message}"</small> : null}
                  </div>
                  <div className="amt">
                    ${d.amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </div>
                  <div className="ago">{timeAgo(d.created_at)}</div>
                </div>
              );
            })}
          </div>
        </>
      ) : null}

      <ProfileShareBar handle={streamer.handle} display={streamer.display_name} />

      {/* Floating "Tip" CTA — visible while scrolling so the donate path is
          always one tap away. Mobile and desktop. */}
      <Link
        to={`/${streamer.handle}/donate`}
        className="prof-fab"
        aria-label={`Tip ${streamer.display_name}`}
      >
        <span className="prof-fab-glyph">▲</span>
        <span className="prof-fab-label">TIP {streamer.display_name.toUpperCase()}</span>
      </Link>

      <footer className="prof-footer">
        <div>BUILT WITH ⚡ ON SUBSWALL · @{streamer.handle.toUpperCase()}</div>
        <div>
          <Link to="/" style={{ color: "var(--text-sec)" }}>← BACK TO HOME</Link>
        </div>
      </footer>
    </div>
  );
}

// Sharebar — copy link, X, Discord. The Discord button just copies a link +
// suggested caption to the clipboard (Discord has no web intent for arbitrary
// channels). Lives at the bottom of the wall so newly-tipping viewers can
// blast their character.
function ProfileShareBar({
  handle,
  display,
}: {
  handle: string;
  display: string;
}): JSX.Element {
  const [copied, setCopied] = useState<null | "link" | "discord">(null);
  const wallUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/${handle}`
      : `https://subswall.com/${handle}`;
  const caption = `Sign ${display}'s wall — your name stays as long as you support.`;

  const copy = async (kind: "link" | "discord"): Promise<void> => {
    const text = kind === "link" ? wallUrl : `${caption}\n${wallUrl}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      // Fallback for browsers that block clipboard without https or perms.
      window.prompt("Copy this link:", text);
    }
  };

  const xIntent =
    `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}` +
    `&url=${encodeURIComponent(wallUrl)}`;

  return (
    <div className="prof-share">
      <div className="prof-share-label">SHARE THIS WALL</div>
      <div className="prof-share-row">
        <button
          type="button"
          className={`prof-share-btn${copied === "link" ? " ok" : ""}`}
          onClick={() => copy("link")}
        >
          {copied === "link" ? "✓ COPIED" : "⎘ COPY LINK"}
        </button>
        <a
          href={xIntent}
          target="_blank"
          rel="noreferrer"
          className="prof-share-btn x"
        >
          𝕏 POST
        </a>
        <button
          type="button"
          className={`prof-share-btn discord${copied === "discord" ? " ok" : ""}`}
          onClick={() => copy("discord")}
        >
          {copied === "discord" ? "✓ COPIED" : "✦ DISCORD"}
        </button>
      </div>
    </div>
  );
}

// Client-side fallback: same shape the server emits via forever_totems.
// Groups permanent donations (source=profile) by donor_handle+faction and
// sums amounts. Keeps the latest message + timestamp per group.
function aggregateClientSide(donations: ProfileDonation[]): ForeverTotemRow[] {
  const groups = new Map<
    string,
    { donor_name: string; donor_handle: string; amount: number; message: string | null; faction_id: string | null; created_at: number }
  >();
  for (const d of donations) {
    // If the server didn't tag source, treat every row as permanent so the
    // wall still renders against legacy data.
    if (d.source && d.source !== "profile") continue;
    const handle = d.donor_name.trim().toLowerCase().replace(/^@/, "");
    const key = `${d.faction_id ?? "_"}::${handle}`;
    const ex = groups.get(key);
    if (ex) {
      ex.amount += d.amount;
      if (d.created_at > ex.created_at) {
        ex.created_at = d.created_at;
        ex.message = d.message;
        ex.donor_name = d.donor_name;
      }
    } else {
      groups.set(key, {
        donor_name: d.donor_name,
        donor_handle: handle,
        amount: d.amount,
        message: d.message,
        faction_id: d.faction_id,
        created_at: d.created_at,
      });
    }
  }
  return [...groups.entries()]
    .map(([_k, g]) => ({
      id: `fw:${g.faction_id ?? "none"}:${g.donor_handle}`,
      donor_name: g.donor_name,
      donor_handle: g.donor_handle,
      amount: Math.round(g.amount * 100) / 100,
      message: g.message,
      faction_id: g.faction_id,
      created_at: g.created_at,
    }))
    .sort((a, b) => b.amount - a.amount);
}
